import { useMemo } from 'react';
import {
    DemeterDataSource,
    DemeterMarket,
    DemeterSubRegion,
    DemeterTableDefinitionModel,
    DemeterTableDefinitionRegionModel,
    DemeterTableDefinitionType,
} from '../../../../Generated/Raven-Demeter/api';
import useTableDefinitionsApi from '../../../Apis/Hooks/useTableDefinitionsApiHook';
import { IRegionCommoditySelection } from './useRegionCommodityNavigationHook';

// A selection without a sub region has to match a definition without sub regions, the backend sends an empty array in that case.
const matchesSubRegion = (definitionSubRegions: DemeterSubRegion[], selectedSubRegion: string): boolean => {
    const subRegions = definitionSubRegions ?? [];

    if (!selectedSubRegion) {
        return subRegions.length === 0;
    }

    return subRegions.some((subRegion) => subRegion === selectedSubRegion);
};

const useTableDefinition = (
    tableDefinitionType: DemeterTableDefinitionType,
    regionCommoditySelection: IRegionCommoditySelection,
    overrideMarket?: DemeterMarket,
): [tableDefinitionRegion?: DemeterTableDefinitionRegionModel, tableDefinitionCommodity?: DemeterTableDefinitionModel] => {
    // We have to use the production table definition when querying for a margin table definition.
    const tableDefinitions = useTableDefinitionsApi(
        tableDefinitionType === DemeterTableDefinitionType.CommodityMonthlyMarginTable
            ? DemeterTableDefinitionType.CommodityMonthlyProductionTable
            : tableDefinitionType,
        false,
        tableDefinitionType === DemeterTableDefinitionType.CommodityPricesTable ||
            tableDefinitionType === DemeterTableDefinitionType.CommodityMonthlyExportsTable ||
            tableDefinitionType === DemeterTableDefinitionType.CommodityMonthlyImportsTable ||
            tableDefinitionType === DemeterTableDefinitionType.CommodityMonthlyDomesticTable ||
            tableDefinitionType === DemeterTableDefinitionType.CommodityMonthlyProductionTable ||
            tableDefinitionType === DemeterTableDefinitionType.CommodityMonthlyStockTable ||
            tableDefinitionType === DemeterTableDefinitionType.CommodityYearlyBalanceTable
            ? DemeterMarket.All
            : overrideMarket,
    );

    const tableDefinitionRegion = useMemo<DemeterTableDefinitionRegionModel | undefined>(
        () => (tableDefinitions ? tableDefinitions.find((x) => x.region === regionCommoditySelection.region) : undefined),
        [tableDefinitions, regionCommoditySelection],
    );

    const tableDefinitionCommodity = useMemo<DemeterTableDefinitionModel | undefined>(() => {
        if (!tableDefinitions) {
            return undefined;
        }

        const matchingTableDefinitionGroups = (tableDefinitionRegion?.demeterTableDefinitionGroups ?? []).filter(
            (group) =>
                group.commodity === regionCommoditySelection.commodity &&
                ((!group.extraParameters && !regionCommoditySelection.extraParameters) ||
                    group.extraParameters === regionCommoditySelection.extraParameters ||
                    (!group.extraParameters && regionCommoditySelection.extraParameters === DemeterDataSource.All)),
        );

        // A commodity can be listed more than once when it is split by sub region, for example Pan European butter in the otc prices.
        const tableDefinitionGroup =
            matchingTableDefinitionGroups.length > 1
                ? matchingTableDefinitionGroups.find((group) => matchesSubRegion(group.subRegions, regionCommoditySelection.subRegion)) ??
                  matchingTableDefinitionGroups[0]
                : matchingTableDefinitionGroups[0];

        if (tableDefinitionGroup) {
            return {
                displayName: tableDefinitionGroup.displayName,
                commodity: tableDefinitionGroup.commodity,
                demeterCommentaryGuid: tableDefinitionGroup.demeterCommentaryGuid,
                extraParameters: tableDefinitionGroup.extraParameters,
                displayDecimalPlacesMaximum: tableDefinitionGroup.displayDecimalPlacesMaximum,
                displayDecimalPlacesMinimum: tableDefinitionGroup.displayDecimalPlacesMinimum,
                subRegions: tableDefinitionGroup.subRegions ?? [],
                isSubGroupHeader: false,
                hasSubGroupBreakBefore: false,
                order: 1,
                dataFrequencies: tableDefinitionGroup.dataFrequencies,
            };
        }

        return tableDefinitionRegion?.demeterTableDefinitionGroups
            .flatMap((group) => group.demeterTableDefinitions.filter((definition) => definition.commodity === regionCommoditySelection.commodity))
            .find(
                (definition) =>
                    (!definition.extraParameters && !regionCommoditySelection.extraParameters) ||
                    definition.extraParameters === regionCommoditySelection.extraParameters ||
                    (!definition.extraParameters && regionCommoditySelection.extraParameters === DemeterDataSource.All),
            );
    }, [tableDefinitions, regionCommoditySelection]);

    return [tableDefinitionRegion, tableDefinitionCommodity];
};

export default useTableDefinition;
